import {
  Alert,
  BottomSheet,
  Button,
  Card,
  Fab,
  FilterChip,
  FilterChipRow,
  StatusPill,
  buttonVariants,
  cn,
} from "@breenova/ui";
import {
  CalendarDays,
  Check,
  ChevronDown,
  Circle,
  Egg as EggIcon,
  Pencil,
  Plus,
  Settings2,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import {
  PageHeader,
  RailGroup,
  RailPrimaryAction,
  RailRow,
  RailSection,
} from "@/components/app-shell";
import { SPLIT_VIEW, useMediaQuery } from "@/lib/media";
import { NestDetailPage } from "@/routes/breeding/nest-detail-page";
import { PairSheet, type PairSheetState } from "@/routes/breeding/pair-sheet";
import {
  isRunningNest,
  seasonLabel,
  useAllNests,
  useBreedingSeasons,
  usePairs,
  type NestSummary,
  type PairSummary,
} from "@/lib/breeding";

type Filter = "running" | "all" | "failed";

const FILTERS: readonly { value: Filter; label: string }[] = [
  { value: "running", label: "Running" },
  { value: "all", label: "All pairs" },
  { value: "failed", label: "Failed" },
];

export function PairsPage() {
  const seasons = useBreedingSeasons();
  const [seasonId, setSeasonId] = useState<string | undefined>(undefined);
  const [filter, setFilter] = useState<Filter>("running");
  const [seasonSheetOpen, setSeasonSheetOpen] = useState(false);
  const [pairSheet, setPairSheet] = useState<PairSheetState | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Falls back to whichever season is current, so the page is useful before the breeder
  // has picked anything.
  const currentSeason = seasons.data?.seasons.find((s) => s.isCurrent);
  const effectiveSeasonId = seasonId ?? currentSeason?.id;
  const season = seasons.data?.seasons.find((s) => s.id === effectiveSeasonId);

  const pairs = usePairs(effectiveSeasonId, false);
  const nests = useAllNests();

  /**
   * The round each pair is on right now. A pair carries no stage of its own — the stage
   * belongs to its latest nest — so the card reads it from here rather than from the pair.
   */
  const latestByPair = useMemo(() => {
    const byPair = new Map<string, NestSummary>();

    for (const nest of nests.data ?? []) {
      const existing = byPair.get(nest.pairId);

      // Latest round wins: a pair on round three has a round-two record that is history.
      if (!existing || nest.round > existing.round) {
        byPair.set(nest.pairId, nest);
      }
    }

    return byPair;
  }, [nests.data]);

  const allPairs = useMemo(() => pairs.data?.items ?? [], [pairs.data]);

  const items = useMemo(() => {
    if (filter === "all") {
      return allPairs;
    }

    return allPairs.filter((pair) => {
      const nest = latestByPair.get(pair.id);

      return filter === "failed" ? nest?.status === "Failed" : Boolean(nest && isRunningNest(nest));
    });
  }, [allPairs, filter, latestByPair]);

  const split = useMediaQuery(SPLIT_VIEW);
  const [selectedPairId, setSelectedPairId] = useState<string | undefined>(undefined);
  const selectedPair = items.find((pair) => pair.id === selectedPairId) ?? items[0];
  const selectedNest = selectedPair ? latestByPair.get(selectedPair.id) : undefined;

  const runningCount = useMemo(
    () =>
      allPairs.filter((pair) => {
        const nest = latestByPair.get(pair.id);
        return Boolean(nest && isRunningNest(nest));
      }).length,
    [allPairs, latestByPair],
  );

  /**
   * Open the pair sheet, or say why it cannot open.
   *
   * A pair belongs to a season, so with none open there is nothing to pair into. That is
   * fixed in the season chooser, which explains the state and carries the link to the
   * screen that opens one — so the tap goes there rather than into a form that would fail
   * on submit.
   */
  function addPair(): void {
    setError(null);

    if (!effectiveSeasonId) {
      setSeasonSheetOpen(true);
      return;
    }

    if (season?.isClosed) {
      setError(
        `The ${seasonLabel(season)} season is closed. Reopen it before adding pairs to it.`,
      );
      return;
    }

    setPairSheet({
      mode: "create",
      seasonId: effectiveSeasonId,
      seasonLabel: season ? seasonLabel(season) : undefined,
    });
  }

  return (
    <div className={split ? "flex min-h-dvh items-start" : "flex flex-col md:gap-lg"}>
      {/* The list column. In the split view it is a 390px column beside the selected
          round; otherwise `contents` dissolves it and nothing about the layout changes. */}
      <div
        className={
          split
            ? "flex w-97.5 shrink-0 flex-col self-stretch border-r border-outline-variant"
            : "contents"
        }
      >
      <RailPrimaryAction>
        <button
          type="button"
          onClick={addPair}
          className={cn(
            "flex h-12 w-full items-center justify-center gap-2 rounded-control",
            "bg-action text-cta-sm text-on-action",
            "transition-opacity hover:opacity-90",
          )}
        >
          <Plus className="size-4.5" aria-hidden />
          New pair
        </button>
      </RailPrimaryAction>

      {/* The chip row's state, in the rail where it stays put. The season sits with the
          filters because it narrows the list the same way they do — it just answers
          "which year" rather than "which state". */}
      <RailSection>
        <RailGroup label="Rounds">
          {FILTERS.map((option) => (
            <RailRow
              key={option.value}
              label={option.label}
              selected={filter === option.value}
              onClick={() => setFilter(option.value)}
              trailing={option.value === "running" ? runningCount : undefined}
            />
          ))}
        </RailGroup>

        <RailGroup label="Season">
          <RailRow
            label={season ? seasonLabel(season) : "No season open"}
            onClick={() => setSeasonSheetOpen(true)}
            leading={<CalendarDays className="size-4 shrink-0" aria-hidden />}
            trailing={<ChevronDown className="size-4" aria-hidden />}
          />
        </RailGroup>
      </RailSection>

      <PageHeader
        compact={split}
        title="Nests"
        // The season is not repeated here: the chip below is what says which year this
        // is, and it is also what changes it.
        subtitle={
          pairs.data
            ? [
                season ? `Season ${seasonLabel(season)}` : null,
                `${pairs.data.totalCount} pairs`,
                `${runningCount} running`,
              ]
                .filter(Boolean)
                .join(" · ")
            : undefined
        }
        /*
         * One calendar affordance on this screen, and it goes to the calendar.
         *
         * There used to be two: this round button, which wore a calendar icon but opened
         * the SEASON chooser, and a small "Calendar" link crowded into the end of the
         * filter row, which was the one that actually went there. Two things labelled
         * calendar, neither reliably the one you wanted, and the link sat in the row the
         * chips scroll through — so with four filters it overlapped them.
         *
         * The season is a filter, so it moved down into the filter row as a chip that
         * says which year it is on. The icon button here now means what it draws.
         */
        action={
          <Link
            to="/breeding/calendar"
            aria-label="Hatches and ringing days"
            className={cn(
              "flex size-11.5 shrink-0 items-center justify-center rounded-full",
              "bg-surface-container-lowest text-on-surface shadow-card",
              "transition-colors hover:bg-surface-container-low",
            )}
          >
            <CalendarDays className="size-4.75" aria-hidden />
          </Link>
        }
      />

      {/* The phone's filters. From `md` the rail carries the same state — and it carries
          it where it stays put, rather than in a row that scrolls out from under the
          thumb the moment the list moves. */}
      <FilterChipRow aria-label="Filter rounds" className="px-5 pb-4 xl:hidden">
        {FILTERS.map((option) => (
          <FilterChip
            key={option.value}
            active={filter === option.value}
            count={option.value === "running" && pairs.data ? runningCount : undefined}
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </FilterChip>
        ))}

        {/* The season, in the row it belongs to. Set apart by a rule rather than by
            being a different kind of control: it narrows the list like the chips beside
            it, it just answers "which year" instead of "which state". */}
        <span aria-hidden className="mx-xs w-px shrink-0 self-stretch bg-outline-variant" />

        <FilterChip
          active={false}
          aria-label={`Breeding season${season ? ` ${seasonLabel(season)}` : ""}. Choose another`}
          onClick={() => setSeasonSheetOpen(true)}
          className="gap-xs"
        >
          <CalendarDays className="size-3.5" aria-hidden />
          <span data-numeric>{season ? seasonLabel(season) : "Season"}</span>
          <ChevronDown className="size-3.5" aria-hidden />
        </FilterChip>
      </FilterChipRow>

      <section
        className={cn("flex flex-col gap-4 px-5", split ? "" : "md:px-gutter-wide")}
        aria-live="polite"
      >
        {error ? <Alert tone="error">{error}</Alert> : null}

        {pairs.isPending ? (
          <p className="py-xl text-center text-body text-on-surface-variant">Loading pairs…</p>
        ) : pairs.isError ? (
          <Alert tone="error">Could not load your pairs.</Alert>
        ) : items.length === 0 ? (
          <Card className="flex flex-col items-center gap-sm p-xl text-center">
            <p className="text-card-title text-on-surface">{emptyTitle(filter)}</p>
            <p className="text-body text-on-surface-variant">{emptyBody(filter)}</p>

            {/* The floating action is easy to miss on a screen that is otherwise empty,
                and this is the one moment the breeder is definitely looking here. Not
                under "Failed", where an empty list is the good outcome and an invitation
                to add something would be answering a question nobody asked. */}
            {filter === "failed" ? null : (
              <Button className="mt-sm" onClick={addPair}>
                <Plus className="size-4" aria-hidden />
                Put a pair together
              </Button>
            )}
          </Card>
        ) : split ? (
          <ul className="flex flex-col gap-2 pb-lg">
            {items.map((pair) => (
              <li key={pair.id}>
                <PairRow
                  pair={pair}
                  nest={latestByPair.get(pair.id)}
                  selected={pair.id === selectedPair?.id}
                  onSelect={() => setSelectedPairId(pair.id)}
                />
              </li>
            ))}
          </ul>
        ) : (
          <ul
            // A pair card is a fixed-height panel, so it tiles: two across on a laptop
            // and three on a wide monitor, rather than one very long column.
            /* Two across on a desktop and no more. The card carries a stage stepper, a
               four-fact strip and a row of rounds; at three across the stepper's labels
               collide and the facts wrap into two lines each. */
            className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-lg"
          >
            {items.map((pair) => (
              <li key={pair.id}>
                <PairCard
                  pair={pair}
                  nest={latestByPair.get(pair.id)}
                  onEdit={() => {
                    setError(null);
                    setPairSheet({ mode: "edit", pair });
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
      </div>

      {split ? (
        <div className="sticky top-0 h-dvh min-w-0 flex-1 overflow-y-auto">
          {selectedNest ? (
            <NestDetailPage id={selectedNest.id} embedded />
          ) : (
            <p className="p-7 text-body text-on-surface-variant">
              {selectedPair ? "This pair has no round yet." : "Choose a pair to see its round."}
            </p>
          )}
        </div>
      ) : null}

      {seasonSheetOpen ? (
        <BottomSheet
          open
          onClose={() => setSeasonSheetOpen(false)}
          title="Breeding season"
          /* The way out of an empty chooser. A breeder with no season yet sees an
             explanation and this button; everyone else sees the way to open next year. */
          footer={
            <Link
              to="/breeding/seasons"
              onClick={() => setSeasonSheetOpen(false)}
              className={cn(buttonVariants({ variant: "secondary" }), "flex-1")}
            >
              <Settings2 className="size-4" aria-hidden />
              Manage seasons
            </Link>
          }
        >
          <ul className="flex flex-col gap-sm">
            {(seasons.data?.seasons ?? []).map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSeasonId(option.id);
                    setSeasonSheetOpen(false);
                  }}
                  className={cn(
                    "flex min-h-touch w-full items-center justify-between gap-md rounded-inset p-md text-left",
                    option.id === effectiveSeasonId
                      ? "bg-secondary-container text-on-secondary-container"
                      : "bg-surface-container-low text-on-surface hover:bg-surface-container",
                  )}
                >
                  <span className="text-row" data-numeric>
                    {seasonLabel(option)}
                  </span>
                  {option.isCurrent ? (
                    <span className="text-label-caps uppercase">Current</span>
                  ) : null}
                </button>
              </li>
            ))}

            {(seasons.data?.seasons ?? []).length === 0 ? (
              <li className="text-body text-on-surface-variant">
                No season has been opened yet. A pair and a round both belong to one, so
                breeding records start here.
              </li>
            ) : null}
          </ul>
        </BottomSheet>
      ) : null}

      {/* The one always-reachable action on this screen. The header slot beside the title
          belongs to the calendar, and a pair is what a breeder comes here to add. */}
      {/* The phone's version of the rail's primary action. Hidden from `md` because the
          rail carries the same button there, and two "new pair" affordances on one screen
          is one of them being missed. */}
      <Fab
        label="New pair"
        icon={<Plus className="size-6" />}
        onClick={addPair}
        className="xl:hidden"
      />

      {pairSheet ? (
        <PairSheet state={pairSheet} onClose={() => setPairSheet(null)} onError={setError} />
      ) : null}
    </div>
  );
}

/**
 * One pair, as the design draws it: who it is, how far the round has got, and the three
 * figures that describe it.
 *
 * A failed round keeps the same shape on a warm ground, with the track stopped at the
 * stage it failed in and the strip saying what comes next — the geometry tells the story
 * as well as a sentence would, and keeps every card in the list scannable the same way.
 */
function PairCard({
  pair,
  nest,
  onEdit,
}: {
  pair: PairSummary;
  nest: NestSummary | undefined;
  onEdit: () => void;
}) {
  const failed = nest?.status === "Failed";
  const ringNow = Boolean(
    nest && nest.chickCount > 0 && nest.expectedRingingOn && nest.expectedRingingOn <= today(),
  );
  const warm = failed || ringNow;
  const cage = pair.locationCode ?? nest?.locationCode;

  return (
    <Card tone={failed ? "warm" : "default"} className="flex h-full flex-col gap-3.5 p-4">
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className={cn(
            "flex size-touch shrink-0 items-center justify-center rounded-full text-disc",
            warm
              ? "bg-secondary-fixed text-on-secondary-fixed"
              : "bg-primary-fixed text-on-primary-fixed",
          )}
          data-numeric
        >
          {cageNumber(cage)}
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-px">
          <Link
            to={`/breeding/pairs/${pair.id}`}
            className="truncate text-pair-title text-on-surface hover:underline"
            data-numeric
          >
            {cage ?? "No cage set"}
          </Link>
          <span className="truncate text-caption text-on-surface-body" data-numeric>
            {pair.sireRingNumber ?? "Unringed"} × {pair.damRingNumber ?? "Unringed"}
            {nest ? ` · round ${nest.round}` : ""}
            {/* A mule pairing is worth calling out: every chick from it is a hybrid. */}
            {pair.isHybridPair ? " · mule pair" : ""}
          </span>
        </div>

        <StatusPill
          tone={ringNow ? "alarm" : statusTone(nest?.status, Boolean(pair.separatedOn))}
        >
          {ringNow ? "Ring now" : (nest?.status ?? (pair.separatedOn ? "Separated" : "Together"))}
        </StatusPill>

        {/* Where the cage, the dates and the separation live. Not a link, because the
            card's own links already go to the rounds — this edits the pair itself. */}
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit the pair ${pair.sireRingNumber ?? "unringed"} by ${pair.damRingNumber ?? "unringed"}`}
          className={cn(
            "-mr-2 flex size-9 shrink-0 items-center justify-center rounded-full",
            "text-on-surface-variant transition-colors hover:bg-surface-container",
          )}
        >
          <Pencil className="size-4" aria-hidden />
        </button>
      </div>

      {nest ? (
        <>
          <Link to={`/breeding/nests/${nest.id}`} className="block">
            <RoundTrack nest={nest} />
          </Link>

          <dl
            className={cn(
              "grid grid-cols-3 rounded-inset px-3.5 py-2.75",
              failed ? "bg-secondary-fixed" : "bg-surface-container",
            )}
            data-numeric
          >
            {stripFacts(nest).map((fact) => (
              <div key={fact.label} className="flex min-w-0 flex-col gap-px">
                <dt className="text-fact-label uppercase text-on-surface-variant">{fact.label}</dt>
                <dd className="truncate text-label text-on-surface">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </>
      ) : (
        <p className="text-detail text-on-surface-variant">
          {pair.eggCount === 0
            ? "No round recorded yet."
            : `${pair.eggCount} eggs and ${pair.hatchedCount} hatched across ${pair.roundCount} rounds.`}
        </p>
      )}
    </Card>
  );
}

/**
 * One pair as a row of the split view's list: the cage, the pair, and where its round
 * stands. The track and the strip are in the pane beside it, so the row only has to say
 * which one to open.
 */
function PairRow({
  pair,
  nest,
  selected,
  onSelect,
}: {
  pair: PairSummary;
  nest: NestSummary | undefined;
  selected: boolean;
  onSelect: () => void;
}) {
  const failed = nest?.status === "Failed";
  const ringNow = Boolean(
    nest && nest.chickCount > 0 && nest.expectedRingingOn && nest.expectedRingingOn <= today(),
  );
  const cage = pair.locationCode ?? nest?.locationCode;

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-card py-3 pl-3 pr-3.5 text-left transition-colors",
        selected
          ? "bg-primary-fixed shadow-[inset_0_0_0_2px_var(--color-accent-mark-soft)]"
          : failed
            ? "bg-secondary-faint shadow-card"
            : "bg-surface-container-lowest shadow-card hover:bg-surface-container-low",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex size-10.5 shrink-0 items-center justify-center rounded-full text-disc",
          failed || ringNow
            ? "bg-secondary-fixed text-on-secondary-fixed"
            : nest && nest.status !== "Building" && nest.status !== "Laying"
              ? "bg-primary-fixed text-on-primary-fixed"
              : "bg-surface-container-low text-on-surface",
        )}
        data-numeric
      >
        {cageNumber(cage)}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="truncate text-label text-on-surface" data-numeric>
          {cage ?? "No cage set"}
        </span>
        <span className="truncate text-caption text-on-surface-variant" data-numeric>
          {pair.sireRingNumber ?? "Unringed"} × {pair.damRingNumber ?? "Unringed"}
        </span>
      </span>

      <StatusPill
        size="sm"
        tone={ringNow ? "alarm" : statusTone(nest?.status, Boolean(pair.separatedOn))}
      >
        {ringNow ? "Ring now" : (nest?.status ?? (pair.separatedOn ? "Separated" : "Together"))}
      </StatusPill>
    </button>
  );
}

/**
 * The five stages of a round as a track of discs: paired, laying, incubating, rearing,
 * fledged. Done stages are filled with a tick, the current one is larger and carries an
 * egg, and a failed round stops on a cross where it failed.
 */
const TRACK_STAGES = ["Paired", "Laying", "Incubating", "Rearing", "Fledged"] as const;

function RoundTrack({ nest }: { nest: NestSummary }) {
  const failed = nest.status === "Failed";
  const current = currentStage(nest);
  const shown = Math.min(current, TRACK_STAGES.length - 1);

  return (
    <ol
      className="flex items-center"
      aria-label={`Round ${nest.round}: ${failed ? "failed while " : ""}${TRACK_STAGES[shown]}, stage ${shown + 1} of ${TRACK_STAGES.length}`}
    >
      {TRACK_STAGES.map((stage, index) => {
        const done = index < current;
        const now = index === current;
        const stopped = failed && now;

        return (
          <li
            key={stage}
            className={cn("flex items-center", index === 0 ? "flex-none" : "flex-1")}
          >
            {index > 0 ? (
              <span
                aria-hidden
                className={cn(
                  "h-1.5 flex-1 rounded-full",
                  done || (now && !failed) ? "bg-accent-mark-soft" : "bg-outline-variant",
                )}
              />
            ) : null}

            <span
              title={stage}
              className={cn(
                "flex shrink-0 items-center justify-center rounded-full",
                now ? "size-10" : "size-8",
                stopped
                  ? "bg-action text-on-action"
                  : done
                    ? "bg-primary text-on-primary"
                    : now
                      ? "bg-primary-fixed-dim text-on-primary-fixed"
                      : "bg-surface-container-low text-outline",
              )}
            >
              {stopped ? (
                <X className="size-4" aria-hidden />
              ) : done ? (
                <Check className="size-4" aria-hidden />
              ) : now ? (
                <EggIcon className="size-4" aria-hidden />
              ) : (
                <Circle className="size-4" aria-hidden />
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Which of the five stages the round is on. Past the last means every stage is done. */
function currentStage(nest: NestSummary): number {
  switch (nest.status) {
    case "Building":
      return 0;
    case "Laying":
      return 1;
    case "Incubating":
      return 2;
    case "Hatching":
      return 3;
    case "Fledged":
      return TRACK_STAGES.length;
    case "Failed":
      // Stopped where it stopped: after a hatch it failed while rearing, before one it
      // failed in the nest.
      return nest.hatchedCount > 0 ? 3 : nest.eggCount > 0 ? 2 : 1;
    default:
      return nest.chickCount > 0 ? 3 : 1;
  }
}

/** The strip's three figures, which change with what the round is doing. */
function stripFacts(nest: NestSummary): readonly { label: string; value: string }[] {
  if (nest.status === "Failed") {
    return [
      // The day it was called is not recorded, so the strip names the round instead.
      { label: "Round", value: `${nest.round}` },
      {
        label: "Eggs",
        value:
          nest.hatchedCount === 0 && nest.eggCount > 0 ? `${nest.eggCount} clear` : `${nest.eggCount}`,
      },
      { label: "Next", value: "Re-pair" },
    ];
  }

  if (nest.chickCount > 0) {
    return [
      { label: "Chicks", value: chickLabel(nest) },
      { label: "Eggs", value: `${nest.eggCount}` },
      { label: "Ring by", value: nest.expectedRingingOn ? dueLabel(nest.expectedRingingOn) : "—" },
    ];
  }

  return [
    { label: "Day", value: dayLabel(nest) },
    { label: "Eggs", value: `${nest.eggCount}` },
    { label: "Hatch", value: nest.expectedHatchOn ? dueLabel(nest.expectedHatchOn) : "—" },
  ];
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** "Today", "Tomorrow", or "Tue 6 Oct" — the way a hatch or a ringing day is said aloud. */
function dueLabel(iso: string): string {
  const days = Math.round((Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${today()}T00:00:00Z`)) / 86_400_000);

  if (days <= 0) {
    return days === 0 ? "Today" : "Overdue";
  }

  if (days === 1) {
    return "Tomorrow";
  }

  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** "3 · day 6" — how many chicks, and how old the eldest is. */
function chickLabel(nest: NestSummary): string {
  if (!nest.firstHatchOn) {
    return `${nest.chickCount}`;
  }

  const age = Math.round(
    (Date.parse(`${today()}T00:00:00Z`) - Date.parse(`${nest.firstHatchOn}T00:00:00Z`)) / 86_400_000,
  );

  return `${nest.chickCount} · day ${Math.max(0, age)}`;
}

function statusTone(
  status: NestSummary["status"] | undefined,
  separated: boolean,
): "neutral" | "positive" | "critical" {
  if (!status) {
    return separated ? "neutral" : "positive";
  }

  switch (status) {
    case "Failed":
      return "critical";
    case "Building":
    case "Unknown":
      return "neutral";
    default:
      return "positive";
  }
}

/** The cage's trailing number, for the disc. "C-14" reads as "14" at 44px. */
function cageNumber(code: string | null | undefined): string {
  if (!code) {
    return "—";
  }

  return /(\d+)\s*$/.exec(code)?.[1] ?? code.slice(-2);
}

function dayLabel(nest: NestSummary): string {
  if (!nest.firstEggOn || !nest.expectedHatchOn) {
    return "—";
  }

  const start = Date.parse(`${nest.firstEggOn}T00:00:00Z`);
  const due = Date.parse(`${nest.expectedHatchOn}T00:00:00Z`);
  const now = Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);

  const total = Math.round((due - start) / 86_400_000);
  const day = Math.round((now - start) / 86_400_000);

  return total > 0 ? `${Math.max(0, day)} of ${total}` : "—";
}

function emptyTitle(filter: Filter): string {
  return filter === "running"
    ? "No round is running"
    : filter === "failed"
      ? "No failed rounds"
      : "No pairs this season";
}

function emptyBody(filter: Filter): string {
  return filter === "running"
    ? "Start a round from a pair to see it here."
    : filter === "failed"
      ? "Nothing has gone wrong this season."
      : "Put a cock and a hen together to start recording rounds.";
}
